/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import React, { ChangeEvent, memo, useEffect, useMemo, useRef, useState } from 'react';
import isFunction from 'lodash/isFunction';
import { Validate } from 'react-hook-form/dist/types/validator';
import { useTranslation } from 'react-i18next';

import {
  calcInputSelection,
  getInputError,
  getInputMaxLength,
  getInputRegisterOptions,
} from 'src/components/UI/inputs/common';
import { FormPropsType } from 'src/components/UI/types';
import { uiMemoFormPropsInputCompareFn } from 'src/components/UI/UIMemoFormPropsCompareFn';
import { UiMereLabel } from 'src/components/UI/UiMereLabel';
import { useIdOrRandomString } from 'src/utils/formHelper';
import { UiHoverTooltip } from 'src/components/UI/HoverTooltip';
import { inputBaseHook } from 'src/utils/hooks/inputs';
import { useHoverErrors } from 'src/components/UI/inputs/useHoverErrors';
import { getTestId } from 'src/utils/testing';
import { tryCatch } from 'src/utils/tryCatch';
import { InputMaterialWrapper } from 'src/components/UI/inputs/common/InputMaterialWrapper';
import { MaterialLabel } from 'src/components/UI/common/MaterialLabel';

type Props = {
  formProps: FormPropsType;
  name: string;

  onChange?: (v: string | null) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  id?: string;
  type?: 'email' | 'text' | 'password' | 'textarea'; // for type="number" use UiFormatNumberInput
  placeholder?: string;
  showAmount?: boolean; // showing by default if maxLength is set
  required?: boolean;
  requiredMessage?: string;
  maxLength?: number;
  minLength?: number;
  validate?: Validate<string>; // the function to validate value (not blocking an input)
  firstCharEachWordUpper?: boolean; // test jest -> Test Jest
  prefix?: string; // input prefix
  pattern?: RegExp; // the regex pattern for the input (blocks an input, allows only input symbols from regexp)
  replaceValue?: (v: string) => string; // the function to replace input value
  className?: string;
  // sets a class and an attribute 'readonly', not an attribute 'disabled' otherwise the value will be lost
  disabled?: boolean;
  customErrorMessage?: string;
  autoFocus?: boolean; // set focus on mount
  label?: string | React.ReactNode;
  errorLabel?: string;
  classNameLabel?: string;
  noParagraph?: boolean;
  defaultValue?: string;
  rows?: number;
  noMaterialize?: boolean;
  showHoverError?: boolean;
  dataTestId?: string;
  material?: boolean; // new field-material design
  onEnter?: () => void;
  defaultPlaceholderStyle?: boolean;
  classNameWrapper?: string;
};

export const UiInput: React.FC<Props> = memo(
  ({
    formProps: {
      setFocus,
      getValues,
      control,
      formState: { errors },
      clearErrors,
    },
    name,
    onChange,
    onBlur,
    maxLength,
    required,
    requiredMessage,
    minLength,
    validate,
    placeholder,
    id: propId,
    type = 'text',
    showAmount = true,
    firstCharEachWordUpper = false,
    prefix,
    pattern,
    replaceValue,
    className,
    disabled,
    customErrorMessage,
    autoFocus,
    label,
    errorLabel,
    classNameLabel,
    noParagraph,
    defaultValue,
    rows,
    noMaterialize,
    showHoverError = false,
    dataTestId,
    material,
    onEnter,
    defaultPlaceholderStyle,
    classNameWrapper,
  }: Props) => {
    const { t } = useTranslation();

    // eslint-disable-next-line no-underscore-dangle
    const _label = 'The field';

    const error = getInputError(errors, name);

    useEffect(() => {
      if (autoFocus) {
        setFocus(name);
      }
    }, [setFocus, name, autoFocus]);

    const [active, setActive] = useState(false);

    const id = useIdOrRandomString(propId);

    const mountRef = useRef(false);

    const inputOptions = useMemo(
      () =>
        getInputRegisterOptions({
          label: errorLabel || name || _label,
          maxLength,
          pattern,
          validate: (v) => {
            if (mountRef.current) {
              if (required && !v) {
                return (
                  requiredMessage || t('common.validation.required', { label: errorLabel || name || _label }).toString()
                );
              }
              if (isFunction(validate)) {
                return validate(v as string);
              }
            }

            return undefined;
          },
          minLength,
          type,
        }),
      [errorLabel, name, maxLength, pattern, minLength, type, required, validate, requiredMessage, t]
    );

    const reg = control.register(name, inputOptions); // must be registered before any other actions

    useEffect(() => inputBaseHook(mountRef, name, clearErrors), [clearErrors, name]);

    const isMaterializeTextarea = type === 'textarea' && !noMaterialize && !material;

    useEffect(() => {
      if (isMaterializeTextarea) {
        tryCatch(() => {
          // a hack for resizing textarea on init
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          $('.materialize-textarea').keydown();
        });
      }
    }, [isMaterializeTextarea]);

    const getInitialOldVal = () => {
      const val = getValues(name) || '';

      /** set only if the value doesn't fit the pattern */
      if (pattern && !pattern.test(val)) {
        return '';
      }

      return val.trim();
    };

    const [oldVal, setOldVal] = useState<string>(getInitialOldVal);

    const countCharsAmount = maxLength && showAmount && active;

    const { hoverMessage, handleChangeInputHover } = useHoverErrors({
      name,
      clearErrors,
      error,
      showAnyError: showHoverError || material,
    });

    const [paddingRight, setPaddingRight] = useState<number | undefined>();

    /** common props */
    const props = {
      id,
      style: { paddingRight },
      className: classNames(className, prefix ? `mp-${prefix.length}` : undefined, {
        required: !!error && required,
        error: !!error || customErrorMessage,
        disabled,
        'material-disabled': disabled && material,
        'no-resize': !isMaterializeTextarea,
        'materialize-textarea': isMaterializeTextarea,
      }),
      'data-name': name,
      maxLength: getInputMaxLength(maxLength),
      placeholder: material && !defaultPlaceholderStyle ? undefined : placeholder,
      readOnly: disabled,
      onKeyPress: ({ preventDefault, key }: React.KeyboardEvent) => {
        if (disabled) {
          preventDefault();
        } else if (key === 'Enter' && isFunction(onEnter)) {
          onEnter();
        }
      },
      onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let changeRootValue = true;

        handleChangeInputHover();

        const {
          target: { value, selectionStart },
        } = e;

        /** handle it all manually */
        let tempValue: string | null = value;

        if (firstCharEachWordUpper) {
          tempValue = tempValue
            .split(' ')
            .map((str) => {
              if (str[0]) {
                const [firstChar, ...otherChars] = str;
                return `${firstChar.toUpperCase()}${otherChars.join('')}`;
              }
              return str;
            })
            .join(' ');
        }

        if (disabled) {
          tempValue = oldVal;
          changeRootValue = false;
        }

        tempValue = tempValue.trimStart(); // string mustn't start with spaces

        /** the second check of maxLength in case of pasting a value (and also for tests) */
        if (maxLength) {
          if (tempValue.length >= maxLength) {
            tempValue = tempValue.substring(0, maxLength);
          }
        }

        if (tempValue.length && pattern) {
          if (!pattern.test(tempValue)) {
            tempValue = oldVal;
            changeRootValue = false;
          }
        }

        /** empty string -> null */
        if (tempValue.length === 0) {
          tempValue = null;
        } else {
          /** remove all spaces(! not \s because of \n) in a row but one */
          tempValue = tempValue.replace(/ +/g, ' ');
        }

        if (isFunction(replaceValue) && tempValue) {
          tempValue = replaceValue(tempValue);
        }

        /** set the value every time or it will have unexpected behaviour */
        e.target.value = tempValue as string; // null is allowed

        if (changeRootValue) {
          reg.onChange(e);
        }

        setOldVal(tempValue || '');

        if (isFunction(onChange)) {
          onChange(tempValue);
        }

        if (selectionStart) {
          // safari https://www.py4u.net/discuss/279152
          e.target.selectionEnd = calcInputSelection(selectionStart, value);
        }
      },
      onFocus: () => {
        if (!disabled) {
          setActive(true);
        }
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!disabled) {
          setActive(false);
          /** if you need a validation on blur, set mode:'onBlur' in form manager */
          // if (required) {
          //   // validate/update required fields
          //   formProps.trigger(name); // affects all form's values
          // }
        }

        reg.onBlur(e);

        if (isFunction(onBlur)) {
          onBlur(e);
        }
      },
    };

    const [inputTestId, charCounterTestId]: [string | undefined, string | undefined] = useMemo(() => {
      if (!dataTestId) {
        return [undefined, undefined];
      }
      const testId = getTestId(type === 'textarea' ? 'input_textarea' : 'input_text', dataTestId, name);
      return [testId(), testId('charcounter')];
    }, [dataTestId, name, type]);

    const InputComponent =
      type === 'textarea' ? (
        // <textarea className="materialize-textarea" />
        <textarea
          {...{ ...reg, ...props }}
          spellCheck="false"
          defaultValue={defaultValue}
          rows={rows}
          data-testid={inputTestId}
        />
      ) : (
        <input type={type} {...{ ...reg, ...props }} defaultValue={defaultValue} data-testid={inputTestId} />
      );

    if (material) {
      return (
        <>
          {label && <MaterialLabel id={id}>{label}</MaterialLabel>}
          <InputMaterialWrapper
            active={active}
            disabled={disabled}
            hasErrors={!!hoverMessage}
            errorMessage={customErrorMessage || hoverMessage}
            placeholder={defaultPlaceholderStyle ? undefined : placeholder}
            hasValue={!!getInitialOldVal().length}
            className={classNameWrapper}
          >
            <div className="input-counter-fix">
              {InputComponent}
              {maxLength && showAmount && (
                <span
                  className={classNames('counter', { 'position-bottom': type === 'textarea' })}
                  data-testid={charCounterTestId}
                  ref={(element) => {
                    if (element) {
                      const prefixWidth = parseInt(window.getComputedStyle(element).getPropertyValue('width'), 10);
                      const prefixPadding = parseInt(
                        window.getComputedStyle(element).getPropertyValue('padding-left'),
                        10
                      );

                      setPaddingRight(prefixWidth + 1 + prefixPadding);
                    }
                  }}
                >
                  {getInitialOldVal().length}&nbsp;/&nbsp;{maxLength}
                </span>
              )}
            </div>
          </InputMaterialWrapper>
        </>
      );
    }

    const Component = (
      <>
        <UiHoverTooltip
          id={name}
          text={hoverMessage}
          render={!!hoverMessage}
          tooltipClassName="error-tooltip"
          noWrapper
        >
          {InputComponent}
        </UiHoverTooltip>

        {customErrorMessage && <span className="fs-12 input-sub-error">{customErrorMessage}</span>}

        {countCharsAmount && (
          <span className="character-counter fs-12" data-testid={charCounterTestId}>
            {getInitialOldVal().length}/{maxLength}
          </span>
        )}
      </>
    );

    const InputLabelWrapper = noParagraph ? React.Fragment : 'p';

    return prefix ? (
      <div className="input-group">
        {prefix && <span className="prefix">{prefix}</span>}
        {Component}
      </div>
    ) : (
      <>
        {label ? (
          <InputLabelWrapper>
            <UiMereLabel className={classNameLabel}>{label}</UiMereLabel>
            {Component}
          </InputLabelWrapper>
        ) : (
          Component
        )}
      </>
    );
  },
  uiMemoFormPropsInputCompareFn
);
